import React from 'react';
import { NumberInput, SliderToggle } from 'venn-components';
import { Button, getAppTitle, Label, Link as LinkStyle, getHelpCenterLinks } from 'venn-ui-kit';
import { FlexWrapper, Info, Name, Section, StyledIcon, WrapperForSections } from '../shared';
import { AdvisoryFeeAction, useAdvisoryFeesSection } from './useAdvisoryFeesSection';
import { ApplyAdvisoryFeesConfirmationModal } from './ApplyAdvisoryFeesConfirmationModal';

export const AdvisoryFeesSection = () => {
  const { config, performAction, canSave, isReadOnly, status, showConfirmation, errors } = useAdvisoryFeesSection();

  return (
    <WrapperForSections data-testid="advisory-fees-section">
      <Section>
        <div className="flex items-center justify-between">
          <div>
            <FlexWrapper>
              <StyledIcon type="circle-dollar" />
              <Name>Report Lab Advisory Fees</Name>
            </FlexWrapper>
            <Info className="mt-2">
              Customize and structure how fees are applied. Investments can also be excluded from fees at a report
              level.{' '}
              <LinkStyle>
                <a target="_blank" rel="noopener noreferrer" href={getHelpCenterLinks().ADVISORY_FEES_HELP_HREF}>
                  Learn More
                </a>
              </LinkStyle>
            </Info>
          </div>
          <Button
            className="[&_i]:text-base [&_i]:-mt-1"
            onClick={() => performAction(AdvisoryFeeAction.ShowConfirmation())}
            icon="save"
            disabled={!canSave}
          >
            {status === 'saving' ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Section>
      <Section>
        <div className="flex items-center justify-between gap-5">
          <div>
            <Label>Display 0% Fees in reports</Label>
            <div className="mt-2 text-sm text-venn-grey-90">
              By default, {getAppTitle()} labels all applicable tables and charts in Report Lab with non-zero advisory
              fees imposed. Toggle &apos;On&apos; to display labels for 0.00% fees as well.
            </div>
          </div>
          <SliderToggle
            toggled={config.displayZeroFee}
            onToggle={() => performAction(AdvisoryFeeAction.ToggleDisplayZeroFee())}
            disabled={isReadOnly}
            dataTestId="display-zero-fee-toggle"
          />
        </div>
      </Section>
      <Section>
        <div className="flex justify-between gap-6">
          <div>
            <Label>Advisory Fee Limits</Label>
            <div className="mt-2 text-sm text-venn-grey-90">
              The Default value is applied when a subject is added to Report Lab. It can be overridden within the
              threshold of the Min and Max values.
            </div>
          </div>
          <div className="flex gap-6">
            <div className="flex flex-col gap-1 w-[100px]">
              <Label>Default</Label>
              <NumberInput
                label="Default fee"
                value={config.defaultFee}
                min={0}
                max={100}
                step={0.1}
                precision={2}
                onChange={(value) => performAction(AdvisoryFeeAction.SetDefaultFee({ value }))}
                disabled={isReadOnly}
                controlled
                height={35}
                unitSymbol="%"
                error={Boolean(errors.defaultFee)}
                align="left"
              />
              {errors.defaultFee ? <div className="text-sm text-venn-error">{errors.defaultFee}</div> : undefined}
            </div>
            <div className="flex flex-col gap-1 w-[100px]">
              <Label>Min</Label>
              <NumberInput
                label="Min fee"
                value={config.minFee}
                min={0}
                max={100}
                step={0.1}
                precision={2}
                onChange={(value) => performAction(AdvisoryFeeAction.SetMinFee({ value }))}
                disabled={isReadOnly}
                controlled
                height={35}
                unitSymbol="%"
                error={Boolean(errors.minFee)}
                align="left"
              />
              {errors.minFee ? <div className="text-sm text-venn-error">{errors.minFee}</div> : undefined}
            </div>
            <div className="flex flex-col gap-1 w-[100px]">
              <Label>Max</Label>
              <NumberInput
                label="Max fee"
                value={config.maxFee}
                min={0}
                max={100}
                step={0.1}
                precision={2}
                onChange={(value) => performAction(AdvisoryFeeAction.SetMaxFee({ value }))}
                disabled={isReadOnly}
                controlled
                height={35}
                unitSymbol="%"
                error={Boolean(errors.maxFee)}
                align="left"
              />
              {errors.maxFee ? <div className="text-sm text-venn-error">{errors.maxFee}</div> : undefined}
            </div>
          </div>
        </div>
      </Section>
      {showConfirmation && (
        <ApplyAdvisoryFeesConfirmationModal
          onApply={() => performAction(AdvisoryFeeAction.Save())}
          onClose={() => performAction(AdvisoryFeeAction.HideConfirmation())}
        />
      )}
    </WrapperForSections>
  );
};
